import { AxiosInstance } from "axios";
import { useCallback } from "react";
import useUser from "./user";
import * as Sentry from "@sentry/react";
import { Caption, LocalStorageKeys } from "../types";
import { jwtDecode } from "jwt-decode";
import amplitude from "../amplitude";

const baseUrl = import.meta.env.VITE_API_GATEWAY_URL ?? "http://localhost:8740";

// TODO: Remove prefix
const prefix = "";

export type SendVerificationCodeRequest = {
  email: string;
};

const useSendVerificationCode = (axios: AxiosInstance) => {
  return useCallback(
    async (request: SendVerificationCodeRequest) => {
      const url = new URL(`${prefix}/v1/auth/sendVerificationCode`, baseUrl);
      return await axios.post(url.toString(), request);
    },
    [axios]
  );
};

export type VerifyCodeRequest = {
  email: string;
  code: string;
};

export type VerifyCodeResponse = {
  encodedToken: string;
};

const useVerifyCode = (axios: AxiosInstance) => {
  return useCallback(
    async (request: VerifyCodeRequest) => {
      const url = new URL(`${prefix}/v1/auth/verifyCode`, baseUrl);
      return await axios.post<VerifyCodeResponse>(url.toString(), request);
    },
    [axios]
  );
};

export type refreshTokenResponse = {
  encodedToken: string;
};

export const useRefreshToken = (axios: AxiosInstance) => {
  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/auth/refreshToken`, baseUrl);
    return await axios.post<refreshTokenResponse>(url.toString());
  }, [axios]);
};

export type User = {
  id: string;
  email: string;
  name: string;
  created_at: Date;
  org_id: string;
  user_type: string;
  languages: string[];
};

type LoginResponse = {
  user: User;
};

const useLogin = (axios: AxiosInstance) => {
  const [, setUser] = useUser();

  const refreshToken = useRefreshToken(axios);

  // Check if email token needs to be refreshed
  const checkRefreshToken = useCallback(async () => {
    // Only handle refresh for email (Langfinity issued) tokens
    const encodedToken = localStorage.getItem(
      LocalStorageKeys.LANGFINITY_TOKEN
    );
    if (!encodedToken) {
      return;
    }
    const decodedToken = jwtDecode(encodedToken);
    if (!decodedToken.exp) {
      return;
    }

    // Check if JWT will expire in 2 days or less
    // Import note: JWTs use seconds from epoch, not ms, convert now to seconds
    const now = Math.floor(Date.now() / 1000);
    if (decodedToken.exp - now > 2 * 24 * 60 * 60) {
      return;
    }

    try {
      const refreshTokenResponse = await refreshToken();
      if (refreshTokenResponse.data.encodedToken) {
        localStorage.setItem(
          LocalStorageKeys.LANGFINITY_TOKEN,
          refreshTokenResponse.data.encodedToken
        );
      }
    } catch (error) {
      // Catch error to avoid failing login
      console.error(error);
      Sentry.captureException(error);
    }
  }, [refreshToken]);

  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/auth/login`, baseUrl).toString();
    const response = await axios.post<LoginResponse>(url);

    setUser((prevState) => ({
      ...prevState,
      auth: {
        ...response.data.user,
      },
      metadata: {
        ...prevState.metadata,
        name: response.data.user.name,
      },
    }));

    Sentry.setUser({
      id: response.data.user.id,
      email: response.data.user.email,
      username: response.data.user.name,
    });
    amplitude?.setUserId(response.data.user.id);

    await checkRefreshToken();

    return response;
  }, [axios, setUser, checkRefreshToken]);
};

export type Room = {
  id: string;
  languages: string[];
  default_voice_on: boolean;
  room_type: "meeting" | "event" | "teams";
  host_id: string;

  name: string | null;
  org_id: string;
  created_at: string;
  meeting_url: string | null;
  auto_record: boolean;
  deep_link: string | null;
  default_voice_avatar: string;
  num_speakers: number | null;
  start_time: Date | string | null;
  end_time: Date | string | null;
  translation_domain: string | null;
  qna: boolean;
  recording_status: "idle" | "initializing" | "recording" | "stopping";
  active_recording_id: string | null;
  reoccurring: boolean;
  available_24_7: boolean;
  pin: string;

  debug: boolean;
};

export type CreateRoomRequest = Pick<Room, "languages" | "room_type"> &
  // Optional
  Partial<
    Pick<
      Room,
      | "name"
      | "start_time"
      | "end_time"
      | "num_speakers"
      | "default_voice_on"
      | "auto_record"
      | "default_voice_avatar"
      | "qna"
      | "deep_link"
      | "reoccurring"
      | "available_24_7"
    >
  >;

export type CreateRoomResponse = {
  room: Room;
};

const useCreateRoom = (axios: AxiosInstance) => {
  return useCallback(
    async (request: CreateRoomRequest) => {
      const url = new URL(`${prefix}/v1/room`, baseUrl).toString();
      return await axios.post<CreateRoomResponse>(url, request);
    },
    [axios]
  );
};

export type GetRoomRequest = {
  id: string;
};

export type GetRoomResponse = {
  room: Room;
  organization: Organization;
  glossaries: Glossary[];
};

const useGetRoom = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GetRoomRequest) => {
      const url = new URL(
        `${prefix}/v1/room/${request.id}`,
        baseUrl
      ).toString();
      return await axios.get<GetRoomResponse>(url);
    },
    [axios]
  );
};

export type GetRoomListResponse = {
  rooms: Room[];
};

const useGetRoomList = (axios: AxiosInstance) => {
  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/room/list`, baseUrl).toString();
    return await axios.get<GetRoomListResponse>(url);
  }, [axios]);
};

export type UpdateRoomRequest = Pick<Room, "id"> & Partial<CreateRoomRequest>;

export type UpdateRoomResponse = {
  room: Room;
};

const useUpdateRoom = (axios: AxiosInstance) => {
  return useCallback(
    async (request: UpdateRoomRequest) => {
      const url = new URL(
        `${prefix}/v1/room/${request.id}`,
        baseUrl
      ).toString();
      return await axios.put<UpdateRoomResponse>(url, request);
    },
    [axios]
  );
};

export type DeleteRoomRequest = { id: string };

const useDeleteRoom = (axios: AxiosInstance) => {
  return useCallback(
    async (request: DeleteRoomRequest) => {
      const url = new URL(
        `${prefix}/v1/room/${request.id}`,
        baseUrl
      ).toString();
      return await axios.delete<DeleteRoomRequest>(url);
    },
    [axios]
  );
};

export type CorrectTranslationRequest = {
  id: string;
  caption: Caption;
  correct_translation: string;
  language: string;
};

const useCorrectTranslation = (axios: AxiosInstance) => {
  return useCallback(
    async (request: CorrectTranslationRequest) => {
      const url = new URL(
        `${prefix}/v1/room/${request.id}/correctTranslation`,
        baseUrl
      ).toString();
      return await axios.post(url, request);
    },
    [axios]
  );
};

export type InviteBotRequest = { roomId: string; meetingUrl: string };

const useInviteBot = (axios: AxiosInstance) => {
  return useCallback(
    async (request: InviteBotRequest) => {
      const url = new URL(
        `${prefix}/v1/bot/${request.roomId}/join`,
        baseUrl
      ).toString();
      return await axios.post(url, request);
    },
    [axios]
  );
};

export type GetBotStatusRequest = { roomId: string };

export type GetBotStatusResponse = { active: boolean };

const useGetBotStatus = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GetBotStatusRequest) => {
      const url = new URL(
        `${prefix}/v1/bot/${request.roomId}/status`,
        baseUrl
      ).toString();
      return await axios.post<GetBotStatusResponse>(url);
    },
    [axios]
  );
};

export type RemoveBotRequest = {
  roomId: string;
};

const useRemoveBot = (axios: AxiosInstance) => {
  return useCallback(
    async (request: RemoveBotRequest) => {
      const url = new URL(
        `${prefix}/v1/bot/${request.roomId}/leave`,
        baseUrl
      ).toString();
      return await axios.post(url);
    },
    [axios]
  );
};

export type ShareDeepLinkRequest = {
  roomId: string;
};

const useShareDeepLinkRequest = (axios: AxiosInstance) => {
  return useCallback(
    async (request: ShareDeepLinkRequest) => {
      const url = new URL(
        `${prefix}/v1/bot/${request.roomId}/shareDeepLink`,
        baseUrl
      );
      return await axios.post(url.toString());
    },
    [axios]
  );
};

export type GenerateLivekitTokenRequest = {
  room: string;
  identity: string;
};

export type GenerateLivekitTokenResponse = {
  encodedToken: string;
};

const useGenerateLivekitToken = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GenerateLivekitTokenRequest) => {
      const url = new URL(`${prefix}/v1/auth/livekitToken`, baseUrl);
      return await axios.post<GenerateLivekitTokenResponse>(
        url.toString(),
        request
      );
    },
    [axios]
  );
};

export type GetUsersByOrgIdRequest = { orgId: string };

export type GetUsersByOrgIdResponse = {
  users: User[];
};

const useGetUsersByOrgId = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GetUsersByOrgIdRequest) => {
      const url = new URL(
        `${prefix}/v1/settings/org/users/${request.orgId}`,
        baseUrl
      );
      return await axios.get<GetUsersByOrgIdResponse>(url.toString());
    },
    [axios]
  );
};

export type RemoveUserFromOrgRequest = { userId: string };

export type RemoveUserFromOrgResponse = { message: string };

const useRemoveUserFromOrg = (axios: AxiosInstance) => {
  return useCallback(
    async (request: RemoveUserFromOrgRequest) => {
      const url = new URL(
        `${prefix}/v1/settings/org/users/${request.userId}`,
        baseUrl
      );
      return await axios.delete<RemoveUserFromOrgRequest>(url.toString());
    },
    [axios]
  );
};

export type UpdateRoomParticipantRequest = {
  room_id: string;
  participant_identity: string;
  language?: string;
  muted?: boolean;
  ai_voice_enabled?: boolean;
};

const useUpdateRoomParticipant = (axios: AxiosInstance) => {
  return useCallback(
    async (request: UpdateRoomParticipantRequest) => {
      const url = new URL(
        `${prefix}/v1/room/${request.room_id}/updateParticipant`,
        baseUrl
      );
      return await axios.post(url.toString(), request);
    },
    [axios]
  );
};

export type CreateStripeCheckoutRequest = {
  quantity: number;
};

export type CreateStripeCheckoutResponse = {
  url?: string;
  message?: string;
};

const useCreateStripeCheckout = (axios: AxiosInstance) => {
  return useCallback(
    async (request: CreateStripeCheckoutRequest) => {
      const url = new URL(
        `${prefix}/v1/stripe/create-checkout-session`,
        baseUrl
      );
      return await axios.post<CreateStripeCheckoutResponse>(
        url.toString(),
        request
      );
    },
    [axios]
  );
};

export type Organization = {
  id: string;
  name: string;
  owner_id: string;

  // Features
  translation_seconds_remaining: number;
  max_room_participants_allowed: number;
  recording_enabled: boolean;
  voice_translation_enabled: boolean;
  voice_avatar_enabled: boolean;
  max_languages_allowed: number;
  max_members_allowed: number;
  glossary_enabled: boolean;
  autodetect_enabled: boolean;
  human_interpreter_enabled: boolean;
  teams_adhoc_enabled: boolean;

  // Settings
  default_languages_setting: string[];
  default_avatar_setting: string;
  default_ai_voice_setting: boolean;
  default_recording_setting: boolean;
  default_qna_setting: boolean;
  default_text_translation_setting: {
    text_size: number;
    text_color: string;
    text_background_color: string;
    text_speed: number;
  };

  created_at: string;

  training_data_opt_in: boolean;

  members: (Pick<User, "id" | "name" | "email"> & { isOwner: boolean })[];
};

export type GetOrganizationResponse = {
  organization: Organization;
  pending_invites: string[];
};

const useGetOrganization = (axios: AxiosInstance) => {
  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/organization`, baseUrl);
    return await axios.get<GetOrganizationResponse>(url.toString());
  }, [axios]);
};

export type UpdateOrganizationRequest = Partial<
  Pick<
    Organization,
    | "default_languages_setting"
    | "default_avatar_setting"
    | "default_ai_voice_setting"
    | "default_recording_setting"
    | "default_qna_setting"
  >
>;

export type UpdateOrganizationResponse = {
  organization: Organization;
};

const useUpdateOrganization = (axios: AxiosInstance) => {
  return useCallback(
    async (request: UpdateOrganizationRequest) => {
      const url = new URL(`${prefix}/v1/organization`, baseUrl).toString();
      return await axios.put<UpdateOrganizationResponse>(url, request);
    },
    [axios]
  );
};

export type Usage = {
  participant_type: "speaker" | "viewer";
  livekit_sid: string | null;
  language: string;
  total_seconds: number;
  created_at: string;
  ended_at: string;
  host_id: string;
  room_id: string;
  email: string;
};

export type GetUsageRequest = {
  user_id?: string;
  start_time?: Date;
  end_time?: Date;
};

export type GetUsageResponse = {
  organization: Organization;
  usage: Usage[];
};

const useGetUsage = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GetUsageRequest) => {
      const url = new URL(`${prefix}/v1/usage`, baseUrl);
      return await axios.get<GetUsageResponse>(url.toString(), {
        params: request,
      });
    },
    [axios]
  );
};

export type GetAdminUsageRequest = {
  org_id: string;
} & GetUsageRequest;

const useGetAdminUsage = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GetAdminUsageRequest) => {
      const url = new URL(`${prefix}/v1/admin/usage`, baseUrl);
      return await axios.get<GetUsageResponse>(url.toString(), {
        params: request,
      });
    },
    [axios]
  );
};

export type Glossary = {
  id: string;
  phrases: string[];
  org_id: string;
  language: string;
  created_at: string;
};

export type GetGlossaryListResponse = {
  glossaries: Glossary[];
};

const useGetGlossaryList = (axios: AxiosInstance) => {
  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/glossary/list`, baseUrl).toString();
    return await axios.get<GetGlossaryListResponse>(url);
  }, [axios]);
};

type StartRecordingRequest = { roomId: string };

const useStartRecording = (axios: AxiosInstance) => {
  return useCallback(
    async (request: StartRecordingRequest) => {
      const url = new URL(
        `${prefix}/v1/recording/start/${request.roomId}`,
        baseUrl
      );
      return await axios.post(url.toString());
    },
    [axios]
  );
};

export type CreateGlossaryRequest = Pick<Glossary, "language" | "phrases">;

export type CreateGlossaryResponse = {
  glossary: Glossary;
};

const useCreateGlossary = (axios: AxiosInstance) => {
  return useCallback(
    async (request: CreateGlossaryRequest) => {
      const url = new URL(`${prefix}/v1/glossary`, baseUrl);
      return await axios.post<CreateGlossaryResponse>(url.toString(), request);
    },
    [axios]
  );
};

type StopRecordingRequest = { roomId: string };

type StopRecordingResponse = { any: unknown };

const useStopRecording = (axios: AxiosInstance) => {
  return useCallback(
    async (request: StopRecordingRequest) => {
      const url = new URL(
        `${prefix}/v1/recording/stop/${request.roomId}`,
        baseUrl
      );
      return await axios.post<StopRecordingResponse>(url.toString());
    },
    [axios]
  );
};
export type UpdateGlossaryRequest = Pick<Glossary, "id" | "phrases">;

export type UpdateGlossaryResponse = {
  glossary: Glossary;
};

const useUpdateGlossary = (axios: AxiosInstance) => {
  return useCallback(
    async (request: UpdateGlossaryRequest) => {
      const url = new URL(`${prefix}/v1/glossary/${request.id}`, baseUrl);
      return await axios.put<UpdateGlossaryResponse>(url.toString(), request);
    },
    [axios]
  );
};

export type DeleteGlossaryRequest = Pick<Glossary, "id">;

const useDeleteGlossary = (axios: AxiosInstance) => {
  return useCallback(
    async (request: DeleteGlossaryRequest) => {
      const url = new URL(`${prefix}/v1/glossary/${request.id}`, baseUrl);
      return await axios.delete(url.toString());
    },
    [axios]
  );
};

export type InviteUserRequest = {
  email: string;
};

export type InviteUserResponse = {
  members: GetOrganizationResponse["organization"]["members"];
  pending_invites: GetOrganizationResponse["pending_invites"];
};

const useInviteUser = (axios: AxiosInstance) => {
  return useCallback(
    async (request: InviteUserRequest) => {
      const url = new URL(`${prefix}/v1/organization/inviteUser`, baseUrl);
      return await axios.post<InviteUserResponse>(url.toString(), request);
    },
    [axios]
  );
};

export type RemoveUserRequest = {
  email: string;
};

export type RemoveUserResponse = {
  members: GetOrganizationResponse["organization"]["members"];
  pending_invites: GetOrganizationResponse["pending_invites"];
};

const useRemoveUser = (axios: AxiosInstance) => {
  return useCallback(
    async (request: RemoveUserRequest) => {
      const url = new URL(`${prefix}/v1/organization/removeUser`, baseUrl);
      return await axios.post<RemoveUserResponse>(url.toString(), request);
    },
    [axios]
  );
};

export type SendMessageRequest = {
  roomId: string;
  messageId: string;
  sourceLanguage: string;
  targetLanguages: string; //comma separated list of languages
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  text: any;
  name: string;
  identity: string;
};

const usePublishMessage = (axios: AxiosInstance) => {
  return useCallback(
    async (request: SendMessageRequest) => {
      const url = new URL(`${prefix}/v1/chat/${request.roomId}`, baseUrl);
      return await axios.post(url.toString(), request);
    },
    [axios]
  );
};

export type Recording = {
  id: string;
  languages: string[];
  created_at: string;
  room_id: string | null;
  room_type: Room["room_type"];
  egress_id: string | null;
  egress_started_at: string | null;
  stopped_at: string | null;
  summaries:
    | { [textLang: string]: string }
    | { loading: true }
    | { error: true }
    | null;
  public: boolean;
  name: string;
};

export type TranscriptCaption = {
  id: string;
  created_at: Date;
  first_created: Date;
  recording_id: string;
  speaker_name: string;
  original_text: string;
  original_lang: string;
  translations: { [language: string]: string };
};

export type GetRecordingRequest = {
  recording_id: string;
};
export type GetRecordingResponse = {
  recording: Recording;
  transcript: TranscriptCaption[];
  signedUrl?: string;
  host?: string;
  participants: { id: string; name: string }[];
  captionCorrections?: number;
};

const useGetRecording = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GetRecordingRequest) => {
      const url = new URL(
        `${prefix}/v1/recording/${request.recording_id}`,
        baseUrl
      );
      return await axios.get<GetRecordingResponse>(url.toString());
    },
    [axios]
  );
};

export type DeleteRecordingRequest = {
  recording_id: string;
};

const useDeleteRecording = (axios: AxiosInstance) => {
  return useCallback(
    async (request: DeleteRecordingRequest) => {
      const url = new URL(
        `${prefix}/v1/recording/${request.recording_id}`,
        baseUrl
      );
      return await axios.delete(url.toString());
    },
    [axios]
  );
};

export type UpdateRecordingVisibilityRequest = {
  recording_id: string;
  public: boolean;
};

const useUpdateRecordingVisibility = (axios: AxiosInstance) => {
  return useCallback(
    async (request: UpdateRecordingVisibilityRequest) => {
      const url = new URL(
        `${prefix}/v1/recording/${request.recording_id}`,
        baseUrl
      );
      return await axios.put(url.toString(), request);
    },
    [axios]
  );
};

export type GenerateSummaryRequest = {
  recordingId: string;
};

const useGenerateSummary = (axios: AxiosInstance) => {
  return useCallback(
    async (request: GenerateSummaryRequest) => {
      const url = new URL(
        `${prefix}/v1/recording/${request.recordingId}/generate-summary`,
        baseUrl
      );
      return await axios.post(url.toString());
    },
    [axios]
  );
};

export type GetRecordingListResponse = {
  recordingList: Recording[];
};
const useGetRecordingList = (axios: AxiosInstance) => {
  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/recording/list`, baseUrl);
    return await axios.get<GetRecordingListResponse>(url.toString());
  }, [axios]);
};

export type LogEventRequest = {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: any;
};

const useLogEvent = (axios: AxiosInstance) => {
  return useCallback(
    async (request: LogEventRequest) => {
      const url = new URL(`${prefix}/v1/log`, baseUrl);
      return await axios.post(url.toString(), request);
    },
    [axios]
  );
};

export type SubmitReferralsRequest = {
  emails: string;
  roomId: string;
};

const useSubmitReferrals = (axios: AxiosInstance) => {
  return useCallback(
    async (request: SubmitReferralsRequest) => {
      const url = new URL(
        `${prefix}/v1/room/${request.roomId}/referral-email`,
        baseUrl
      );
      return await axios.post(url.toString(), request);
    },
    [axios]
  );
};

export type UpdateSessionRequest = {
  client_id: string;
};

const useUpdateSessionRequest = (axios: AxiosInstance) => {
  return useCallback(
    async (request: UpdateSessionRequest) => {
      const url = new URL(`${prefix}/v1/auth/session`, baseUrl);
      return await axios.post(url.toString(), request);
    },
    [axios]
  );
};

export type GetOrgListResponse = {
  orgs: { id: string; email: string }[];
};

const useGetOrgList = (axios: AxiosInstance) => {
  return useCallback(async () => {
    const url = new URL(`${prefix}/v1/admin/org/list`, baseUrl).toString();
    return await axios.get<GetOrgListResponse>(url);
  }, [axios]);
};

const api = {
  useSendVerificationCode,
  useVerifyCode,
  useLogin,
  useCreateRoom,
  useGetRoom,
  useGetRoomList,
  useUpdateRoom,
  useDeleteRoom,
  useCorrectTranslation,
  useInviteBot,
  useGetBotStatus,
  useRemoveBot,
  useShareDeepLinkRequest,
  useGenerateLivekitToken,
  useGetUsersByOrgId,
  useRemoveUserFromOrg,
  useUpdateRoomParticipant,
  useCreateStripeCheckout,
  useGetOrganization,
  useStartRecording,
  useStopRecording,
  useUpdateOrganization,
  useGetUsage,
  useGetGlossaryList,
  useCreateGlossary,
  useUpdateGlossary,
  useDeleteGlossary,
  useInviteUser,
  useRemoveUser,
  useGetRecording,
  useDeleteRecording,
  usePublishMessage,
  useGenerateSummary,
  useUpdateRecordingVisibility,
  useGetRecordingList,
  useLogEvent,
  useSubmitReferrals,
  useUpdateSessionRequest,
  useGetOrgList,
  useGetAdminUsage,
};

export default api;
